import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { List } from 'immutable';
import { castArray } from 'lodash';
import { mergeConfig } from '../api/utils';
import { Relations } from './Relations';

export abstract class Model<T extends Model = any> {
    protected abstract readonly blueprint: { new (...args: any): T };
    protected abstract readonly endpoint: AxiosInstance;

    public id!: number;
    public created_at: any;
    public updated_at: any;

    constructor(values?: T) {
        this.from = this.from.bind(this);
        if (values) {
            return this.from(values);
        }
    }

    public from(this: this, data: T): this {
        const values = (data?.toJS ? data.toJS() : data) || {};
        const relationshipsFunc = (this as Partial<Relations<any>>)?.relations;
        const relationships = relationshipsFunc ? relationshipsFunc() : [];

        Object.entries(values).forEach(([prop, value]: [string, any]) => {
            const relationship = relationships.find(({ key }) => key === prop);

            if (relationship) {
                if (relationship.collection) {
                    (this as any)[prop] = castArray(value).map((val) => new relationship.model().from(val));
                } else {
                    (this as any)[prop] = new relationship.model().from(value);
                }
            } else {
                (this as any)[prop] = value;
            }
        });

        return this;
    }

    /**
     * Return a collection of models
     * @param collection
     */
    public fromCollection(collection: null | any[]) {
        return (collection ?? []).map((data) => new this.blueprint().from(data));
    }

    toJS(): T {
        return JSON.parse(JSON.stringify(this));
    }

    /**
     * Send a get request to grab a list of resources
     * @param config
     */
    public async index(config?: AxiosRequestConfig): Promise<List<this>> {
        const { data } = await this.endpoint.get('', config);
        let list = List<this>();

        const items = data?.items || data?.data || data;
        if (Array.isArray(items)) {
            const ItemModel = this.blueprint;
            let instance: any;

            items.forEach((item) => {
                instance = new ItemModel();
                list = list.push(instance.from(item));
            });
        }

        return list;
    }

    /**
     * Grabbing all items with a index request
     * @param config
     */
    public async all(config?: AxiosRequestConfig): Promise<List<this>> {
        return this.index(
            mergeConfig(config, {
                params: { all: true },
            }),
        );
    }

    /**
     * Send a get request to grab a resource by id
     * @param id
     * @param config
     */
    public async find(id: any, config?: AxiosRequestConfig): Promise<this> {
        id = `${id}`.split('/')[0];
        const { data } = await this.endpoint.get(`/${id}`, config);
        const ItemModel = this.blueprint;
        const instance = new ItemModel();

        return instance.from(data) as any;
    }

    /**
     * Send a get request to grab a list of resources
     * @param record
     * @param config
     */
    public async create(record: Partial<this>, config?: AxiosRequestConfig): Promise<this> {
        const { data } = await this.endpoint.post('', record, config);
        return this.from(data);
    }
}
